import React, { ChangeEvent, ReactNode } from "react";
import DatePicker from "react-datepicker";
import i18n from "@i18n";
import { addMonths, LONG_DATE_FORMAT } from "@utils/datetime";
import { recommendPlan } from "@utils/calculateGoals";
import { trackEvent } from "@utils/api/tracker";
import RadioWithInputOther, {
  RadioWithInputOtherQuestionDefinition,
} from "../shared-inputs/RadioWithInputOther";
import Question from "../../../question-types/Question";

import styles from "./Dates.module.less";
import "react-datepicker/dist/react-datepicker.css";
import {
  SurveyAnswer,
  SurveyAnswersState,
} from "src/utils/redux/slices/surveyAnswers";
import {
  CustomQuestionDefinition,
  QuestionProps,
  registerQuestion,
} from "src/components/refactored-survey/QuestionDefinition";
import {
  question,
  questionTitle,
  questionOptions,
} from "@components/refactored-survey/question-types/Question.module.less";
import {
  questionInput,
  inputWithUnit,
  unitLabel,
  valid,
} from "@components/refactored-survey/question-types/InputQuestion.module.less";
import Calendar from "./images/calendar.svg";
import { isMobile } from "src/utils/userSegment";
import { Text } from "src/components/core/Text";
import { getLocale } from "src/utils/meristemContext";

type DateQuestionDefinition = RadioWithInputOtherQuestionDefinition & {
  question: (surveyAnswer: SurveyAnswer) => ReactNode;
};

class ImportantDateDetails extends RadioWithInputOther<DateQuestionDefinition> {
  override handleClick(e) {
    const ans: SurveyAnswer = {};

    ans[this.props.question.id] = [e.target.value];
    const answerIsValid = this.validateAnswer(ans);
    ans.importantDateSubDetails = undefined;
    ans.importantDate = e.target.value === "none" ? ["no"] : ["yes"];
    this.props.registerAnswer(ans, answerIsValid);
  }

  override handleOtherInputChange(e: ChangeEvent<HTMLInputElement>) {
    super.handleOtherInputChange(e);

    const { value } = e.target;
    this.setState({ otherInput: value });
    const ans = { [this.props.question.id]: [this.otherFieldId, value] };
    const isValid = this.validateAnswer(ans);

    ans.importantDateSubDetails = [value];
    ans.importantDate = ["yes"];
    this.props.registerAnswer(ans, isValid);
  }
}

const OPTION_KEYS = [
  "vacation",
  "wedding",
  "holiday",
  "summer",
  "reunion",
  "birthday",
];

export function getImportantEventText(surveyAnswers: SurveyAnswersState) {
  let answer = surveyAnswers.importantDateDetails?.[0];
  if (!OPTION_KEYS.includes(answer)) {
    answer = "unknown";
  }

  return i18n.t(`survey:questions:importantDate:options:${answer}`);
}

export function getImportantEventTextLong(surveyAnswers: SurveyAnswersState) {
  let answer = surveyAnswers.importantDateDetails?.[0];
  if (!OPTION_KEYS.includes(answer)) {
    answer = "unknown";
  }

  return i18n.t(`survey:questions:importantDate:importantDateLong:${answer}`);
}

export const Q_IMPORTANT_DATE_DETAILS =
  registerQuestion<RadioWithInputOtherQuestionDefinition>({
    id: "importantDateDetails",
    type: "custom",
    component: ImportantDateDetails,
    question: () => {
      return (
        <>
          <h1 className={questionTitle}>
            {i18n.t("survey:questions:importantDate:title")}
          </h1>
          <h2 className={questionTitle}>
            {i18n.t("survey:questions:importantDate:question")}
          </h2>
        </>
      );
    },
    options: OPTION_KEYS.map((key) => ({
      id: key,
      text: i18n.t(`survey:questions:importantDate:options:${key}`),
    })),
    belowOtherOptions: [
      {
        id: "none",
        text: i18n.t("survey:questions:importantDate:options:none"),
      },
    ],
    section: 1,
  });

interface CalendarContainerProps {
  className?: string;
  children: ReactNode;
}

// Centers the desktop date picker pop up with the input.
const CalendarContainer = ({ className, children }: CalendarContainerProps) => (
  <div className={styles.calendarContainer}>
    <div className={className}>{children}</div>
  </div>
);

export class DatePickerQuestion extends Question<CustomQuestionDefinition> {
  constructor(props: QuestionProps) {
    super(props);

    const storeDate = this.props.surveyAnswers?.importantDateTime;
    this.state = {
      date: storeDate ? new Date(storeDate) : null,
    };

    this.onDateChange = this.onDateChange.bind(this);
    this.handleSkip = this.handleSkip.bind(this);
  }

  // eslint-disable-next-line class-methods-use-this
  override onMount() {
    // Hack to disable keyboard pop up on mobile when user clicks on date field
    // https://stackoverflow.com/questions/48805053/hide-native-keyboard-on-mobile-when-using-react-date-picker
    const datePickers = document.getElementsByClassName(
      "react-datepicker__input-container"
    );
    Array.from(datePickers).forEach((el) => {
      if (el.childNodes[0]) {
        (el.childNodes[0] as HTMLElement).setAttribute("readOnly", "readOnly");
      }
    });
  }

  onDateChange(date: Date) {
    this.setState({ date }, () => {
      const { recommendedPlanDuration } = recommendPlan();
      const planEndDate = addMonths(new Date(), recommendedPlanDuration);
      trackEvent("ImportantDateComparedToPlanDate", {
        date: date > planEndDate ? "after" : "before",
      });

      const formattedDate = date
        ? new Date(date).toLocaleDateString(getLocale(), LONG_DATE_FORMAT)
        : null;
      this.props.registerAnswer({ importantDateTime: formattedDate }, !!date);
    });
  }

  override validateAnswer() {
    return !!this.state.date;
  }

  handleSkip() {
    trackEvent("SkippedImportantDateTime");
    this.props
      .registerAnswer({ importantDateTime: null }, true)
      .then(this.props.onClickNext);
  }

  override renderControls() {
    return (
      <>
        {super.renderControls()}
        <button
          className={styles.datePickerSkip}
          onClick={this.handleSkip}
          type="button"
        >
          <Text i18nKey="survey:questions:importantDate:skipQuestion" />
        </button>
      </>
    );
  }

  override renderWithControls() {
    return (
      <div
        data-cy="question"
        data-cy-question={this.props.question.id}
        className={question}
      >
        {this.renderQuestion()}
        <div
          className={`${questionOptions} ${inputWithUnit} ${styles.dateWrapper}`}
        >
          <DatePicker
            value={this.state.date}
            selected={this.state.date}
            onChange={this.onDateChange}
            dateFormat="MMMM d yyyy"
            placeholderText="Select a date"
            isClearable
            // @ts-expect-error Not defined in typings but implemented in react-datepicker
            style={{ width: "100%" }}
            id="important-date-picker"
            className={`${questionInput} ${this.state.date && valid}`}
            withPortal={isMobile()}
            onFocus={(e) => {
              // eslint-disable-next-line no-param-reassign
              e.target.readOnly = true;
            }}
            calendarContainer={CalendarContainer}
          />
          <img
            src={Calendar}
            alt=""
            className={`${unitLabel} ${styles.datePickerIcon}`}
            // Hide calendar icon when date is selected so it doesn't block
            // the button to clear the field.
            style={this.state.date ? { display: "none" } : {}}
          />
        </div>
      </div>
    );
  }
}

export const Q_IMPORTANT_DATE_TIME = registerQuestion({
  id: "importantDateTime",
  type: "custom",
  component: DatePickerQuestion,
  question: ({ surveyAnswers }) => {
    let answer = surveyAnswers.importantDateDetails?.[0];
    if (!OPTION_KEYS.includes(answer)) {
      answer = "unknown";
    }

    return i18n.t(`survey:questions:importantDateTime:${answer}`);
  },
  section: 1,
});
